
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';
import { Eye } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';

interface RealtimeViewerCountProps {
  streamId: number;
  fallbackCount?: number;
  showLiveIndicator?: boolean;
  className?: string;
}

export function RealtimeViewerCount({
  streamId,
  fallbackCount = 0,
  showLiveIndicator = true,
  className = ""
}: RealtimeViewerCountProps) {
  const [viewerCount, setViewerCount] = useState<number | undefined>(fallbackCount);
  const [displayCount, setDisplayCount] = useState(fallbackCount);

  const handleMessage = useCallback((message: any) => {
    if (message.type === 'viewer_count_update' && message.streamId === streamId) {
      setViewerCount(message.viewerCount);
    }
  }, [streamId]);

  const { isConnected } = useDurableObjectChannel({
    channel: `stream:viewers:${streamId}`,
    enabled: !!streamId,
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (viewerCount !== undefined && viewerCount >= 0) {
      setDisplayCount(viewerCount);
    } else if (fallbackCount !== undefined) {
      setDisplayCount(fallbackCount);
    }
  }, [viewerCount, fallbackCount]);

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Eye className="h-3 w-3" />
      <span>{displayCount}</span>
      {showLiveIndicator && isConnected && (
        <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" title="Live count" />
      )}
    </div>
  );
}